import { createDiscardConfirmation } from "./discardConfirmation.ts";
import { createUnsavedIndicator } from "./editorChrome.ts";
import { appendCatalogProductFields, readCatalogProductFields } from "./catalogProductFields.ts";
import { hoverHelp, hideHoverHelp } from "./hoverHelp.ts";
import { createSetupDisclosure } from "./setupDisclosure.ts";
import {
  catalogPackage,
  catalogProposal,
  catalogCompatible,
  keyFromName,
  isCatalogView,
  isCatalogActivationStatus,
  isCatalogActivationResult,
  type CatalogActivationIdentity,
  type CatalogActivationIntent,
  type CatalogActivationStatus,
  type CatalogView,
  type CatalogProduct,
  type CatalogProposal,
  type CatalogRequest,
  type CatalogLocalItem,
  type CatalogReply,
  type CatalogLocalCreateSelection,
} from "./catalogNetworkModel.ts";
export interface CatalogNetworkDeps {
  identity(): string | null;
  activationIdentity?(): CatalogActivationIdentity | null;
  canView(): boolean;
  canManage(): boolean;
  network: boolean;
  prepareLocalItem?(
    selection: CatalogLocalCreateSelection,
    isCurrent: () => boolean,
    completed: () => void,
  ): void;
  request(method: "GET" | "POST", path: string, body?: unknown): Promise<CatalogReply>;
}
function node<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  text?: string,
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (text !== undefined) e.textContent = text;
  return e;
}
export function mountCatalogNetwork(host: HTMLElement, deps: CatalogNetworkDeps) {
  let generation = 0,
    view: CatalogView | undefined,
    requests: CatalogRequest[] = [],
    items: CatalogLocalItem[] = [],
    busy = false,
    blocked = false,
    query = "",
    kindFilter = "",
    status = "";
  // INVUX1 (finding 01): a failed READ keeps what was loaded and offers Retry. While it is set nothing is written:
  // the kept rows may be out of date, and an unconfirmed write keeps its own Reload recovery (blocked).
  let loadFailed = false;
  let editor: HTMLElement | undefined;
  let editorForm: HTMLFormElement | undefined;
  let editorInitial: string | undefined;
  function formValues(form: HTMLFormElement): string {
    return JSON.stringify([...form.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>("input,select,textarea")].map((control) =>
      [control.name, control instanceof HTMLInputElement && (control.type === "checkbox" || control.type === "radio") ? control.checked : control.value],
    ));
  }
  const discardConfirmation = createDiscardConfirmation();
  async function confirmLeave(): Promise<boolean> {
    if (busy) return false;
    const id = deps.identity(), g = generation, form = editorForm;
    const stillCurrent = () => deps.identity() === id && generation === g &&
      editorForm === form && !busy;
    if (form && editorInitial !== undefined && formValues(form) !== editorInitial) {
      if (!(await discardConfirmation.ask(stillCurrent))) return false;
    }
    return stillCurrent();
  }
  async function closeEditor(): Promise<void> {
    if (await confirmLeave()) clearEditor();
  }
  let requestsAvailable = false;
  let candidatesAvailable = false;
  let candidatesMessage = "";
  let catalogExpanded = false;
  let catalogIdentity: string | null = null;
  const workspaceId = `catalog-workspace-${crypto.randomUUID()}`;
  type PendingActivation = {
    intent: Readonly<CatalogActivationIntent>;
    outcome: "unknown" | "retry_same_request" | "not_applied";
  };
  const pendingActivations = new Map<string, PendingActivation>();
  let activationStatus: CatalogActivationStatus | undefined;
  let activationMessage = "";
  let activationSequence = 0;
  const activationScope = (): string | null => {
    const actor = deps.network ? deps.activationIdentity?.() : null;
    return actor?.networkId && actor.accountId
      ? JSON.stringify([actor.networkId, actor.accountId])
      : null;
  };
  const pendingActivation = () => {
    const scope = activationScope();
    return scope === null ? undefined : pendingActivations.get(scope);
  };
  host.classList.add("catalog-network");
  const current = (id: string | null, g: number) =>
    id !== null && deps.identity() === id && generation === g && deps.canView();
  const manages = () =>
    !blocked &&
    !loadFailed &&
    view !== undefined &&
    deps.canManage() &&
    (deps.network || view.ownerKind === "independent");
  const localWrites = () =>
    !blocked && !loadFailed && view !== undefined && deps.canManage() && !deps.network;
  function button(label: string, act: () => void, disabled = false) {
    const b = node("button", label);
    b.type = "button";
    b.disabled = disabled || busy;
    b.onclick = act;
    return b;
  }
  function clearEditor() {
    discardConfirmation.cancel();
    hideHoverHelp();
    if (editor instanceof HTMLDialogElement && editor.open) editor.close();
    editor?.remove();
    editor = undefined;
    editorForm = undefined;
    editorInitial = undefined;
  }
  function errorText(body: unknown): string {
    return body !== null &&
      typeof body === "object" &&
      "message" in body &&
      typeof body.message === "string"
      ? body.message
      : "Catalog unavailable. Reload before trying again.";
  }
  /** INVUX1: why a read failed, in words that never say to recreate anything. */
  function readFailedText(body: unknown, kept: boolean): string {
    if (kept)
      return "Could not refresh the catalog. Showing what was loaded earlier. Retry before making changes.";
    return body !== null &&
      typeof body === "object" &&
      "message" in body &&
      typeof body.message === "string"
      ? body.message
      : "The catalog could not be loaded.";
  }
  function clearKnown() {
    view = undefined;
    items = [];
    requests = [];
    requestsAvailable = false;
    candidatesAvailable = false;
    candidatesMessage = "";
  }
  async function load(reloadResolved = false, feedback = "") {
    const id = deps.identity(),
      g = ++generation;
    clearEditor();
    // INVUX1: a refresh for the same signed-in scope keeps the rows already read, drawn read-only, until the new read
    // lands. A different scope, or no access, never shows another scope's data.
    const keep = id !== null && catalogIdentity === id && view !== undefined && deps.canView();
    if (catalogIdentity !== id) catalogExpanded = false;
    catalogIdentity = id;
    status = feedback;
    activationSequence++;
    activationStatus = undefined;
    const scope = activationScope();
    const original = scope === null ? undefined : pendingActivations.get(scope);
    activationMessage = original
      ? "Activation unconfirmed. Check its status before continuing."
      : "";
    blocked = false;
    busy = false;
    loadFailed = false;
    if (!id || !deps.canView()) {
      host.replaceChildren();
      clearKnown();
      host.hidden = true;
      return;
    }
    host.hidden = false;
    if (keep) {
      status = "Refreshing the catalog…";
      render();
      renderBusy();
    } else {
      host.replaceChildren();
      clearKnown();
      const loading = node("p", "Loading catalog…");
      loading.setAttribute("role", "status");
      host.append(loading);
    }
    let primaryRead = false;
    try {
      const result = await deps.request("GET", "");
      if (!current(id, g)) return;
      if (result.status !== 200 || !isCatalogView(result.body)) {
        if (result.status === 403) {
          // Access changed: nothing private stays on screen.
          clearKnown();
          blocked = true;
          status = errorText(result.body);
        } else {
          loadFailed = true;
          status = readFailedText(result.body, view !== undefined);
        }
        render();
        return;
      }
      status = feedback;
      view = result.body;
      primaryRead = true;
      items = [];
      requests = [];
      requestsAvailable = false;
      candidatesAvailable = false;
      candidatesMessage = "";
      const inbox =
        !deps.network || deps.canManage() ? await deps.request("GET", "/requests") : null;
      if (!current(id, g)) return;
      if (inbox?.status === 403) {
        blocked = true;
        status = "Catalog access changed. Reload to view current access.";
      }
      if (
        inbox?.status === 200 &&
        inbox.body !== null &&
        typeof inbox.body === "object" &&
        "requests" in inbox.body &&
        Array.isArray(inbox.body.requests)
      ) {
        requests = inbox.body.requests as CatalogRequest[];
        requestsAvailable = true;
      } else if (inbox !== null && inbox.status !== 403) {
        status = "Product requests unavailable. Reload before reviewing requests.";
      }
      if (!deps.network) {
        const candidates = await deps.request("GET", "/candidates");
        if (!current(id, g)) return;
        if (
          candidates.status === 200 &&
          candidates.body !== null &&
          typeof candidates.body === "object" &&
          "items" in candidates.body &&
          Array.isArray(candidates.body.items)
        ) {
          items = candidates.body.items as CatalogLocalItem[];
          candidatesAvailable = true;
        } else {
          blocked = true;
          candidatesMessage = errorText(candidates.body);
        }
      }
      if (deps.network && scope !== null && deps.canManage()) {
        await readActivation(id, g, scope, original, reloadResolved);
        if (!current(id, g) || activationScope() !== scope) return;
      }
      render();
    } catch {
      if (current(id, g)) {
        loadFailed = true;
        status = primaryRead
          ? "Part of the catalog could not be read. Retry to load all of it."
          : readFailedText(null, view !== undefined);
        render();
      }
    }
  }
  async function readActivation(
    id: string,
    g: number,
    scope: string,
    original: PendingActivation | undefined,
    reloadResolved = false,
  ): Promise<void> {
    const sequence = ++activationSequence;
    const reload = reloadResolved && original?.outcome === "not_applied";
    const reply = await deps.request(
      original && !reload ? "POST" : "GET",
      "/cutover-status",
      original && !reload ? original.intent : undefined,
    );
    if (
      !current(id, g) ||
      activationScope() !== scope ||
      sequence !== activationSequence ||
      pendingActivations.get(scope) !== original
    )
      return;
    if (reply.status !== 200 || !isCatalogActivationStatus(reply.body)) {
      if (original)
        original.outcome = original.outcome === "not_applied" ? "not_applied" : "unknown";
      activationMessage =
        reply.status === 401 || reply.status === 403
          ? "Sign in with catalog management access to check activation status."
          : original
            ? "Activation status unavailable. Check status again."
            : "Activation status unavailable. Reload to check again.";
      return;
    }
    const result = reply.body;
    if (original && !reload) {
      if (result.catalog?.id !== original.intent.catalogId || result.outcome === undefined) {
        original.outcome = "unknown";
        activationMessage = "Original activation remains unconfirmed. Check status again.";
        return;
      }
      if (
        result.outcome === "applied" &&
        result.resultingVersion === original.intent.expectedVersion + 1
      ) {
        pendingActivations.delete(scope);
        activationStatus = result;
        activationMessage = "Catalog is active.";
      } else if (
        result.outcome === "not_applied" &&
        result.catalog.version > original.intent.expectedVersion
      ) {
        original.outcome = "not_applied";
        activationStatus = result;
        activationMessage =
          "Previous activation was not applied. Reload the current catalog before reviewing activation again.";
      } else if (
        result.outcome === "retry_same_request" &&
        result.catalog.version === original.intent.expectedVersion
      ) {
        original.outcome = "retry_same_request";
        activationStatus = result;
        activationMessage = "Still in preparation. You can retry the same activation.";
      } else {
        original.outcome = "unknown";
        activationMessage = "Original activation remains unconfirmed. Check status again.";
      }
      return;
    }
    if (
      result.outcome !== undefined ||
      (view?.catalog == null
        ? result.catalog !== null
        : result.catalog?.id !== view.catalog.id ||
          result.catalog.state !== view.catalog.state ||
          result.catalog.version !== view.catalog.version)
    ) {
      activationMessage = "Catalog changed. Reload before reviewing activation.";
      return;
    }
    if (reload && original) {
      if (
        result.catalog === null ||
        result.catalog.id !== original.intent.catalogId ||
        result.catalog.version <= original.intent.expectedVersion
      ) {
        activationMessage = "Current catalog could not be reconciled. Reload again.";
        return;
      }
      pendingActivations.delete(scope);
    }
    activationStatus = result;
  }
  async function checkActivation(): Promise<void> {
    const id = deps.identity(),
      scope = activationScope(),
      g = generation;
    const original = scope === null ? undefined : pendingActivations.get(scope);
    if (!id || scope === null || !original || busy || !deps.canManage()) return;
    busy = true;
    renderBusy();
    try {
      await readActivation(id, g, scope, original);
    } catch {
      if (
        current(id, g) &&
        activationScope() === scope &&
        pendingActivations.get(scope) === original
      ) {
        original.outcome = original.outcome === "not_applied" ? "not_applied" : "unknown";
        activationMessage = "Activation status unavailable. Check status again.";
      }
    } finally {
      if (current(id, g) && activationScope() === scope) {
        busy = false;
        render();
      }
    }
  }
  async function submitActivation(scope: string, original: PendingActivation): Promise<void> {
    const id = deps.identity(),
      g = generation;
    if (
      !id ||
      activationScope() !== scope ||
      pendingActivations.get(scope) !== original ||
      busy ||
      !deps.canManage()
    )
      return;
    clearEditor();
    busy = true;
    original.outcome = "unknown";
    activationMessage = "Activation unconfirmed. Check its status before continuing.";
    render();
    renderBusy();
    try {
      const reply = await deps.request("POST", "/cutover", original.intent);
      if (
        !current(id, g) ||
        activationScope() !== scope ||
        pendingActivations.get(scope) !== original
      )
        return;
      if (
        reply.status === 200 &&
        isCatalogActivationResult(reply.body) &&
        reply.body.id === original.intent.catalogId &&
        reply.body.version === original.intent.expectedVersion + 1
      ) {
        pendingActivations.delete(scope);
        activationMessage = "Catalog activated.";
        busy = false;
        await load();
        return;
      }
      activationMessage =
        reply.status === 401 || reply.status === 403
          ? "Sign in with catalog management access to check the original activation."
          : "Activation unconfirmed. Check its status before continuing.";
    } catch {
      if (
        current(id, g) &&
        activationScope() === scope &&
        pendingActivations.get(scope) === original
      )
        activationMessage = "Activation unconfirmed. Check its status before continuing.";
    } finally {
      if (current(id, g) && activationScope() === scope) {
        busy = false;
        render();
      }
    }
  }
  async function confirmActivation(): Promise<void> {
    const scope = activationScope(),
      id = deps.identity(),
      g = generation;
    const catalog = activationStatus?.catalog;
    if (
      !id ||
      scope === null ||
      pendingActivations.has(scope) ||
      busy ||
      blocked ||
      !deps.canManage() ||
      !activationStatus?.ready ||
      catalog?.state !== "preparation" ||
      catalog.id !== view?.catalog?.id ||
      catalog.version !== view.catalog.version
    )
      return;
    if (!(await confirmLeave())) return;
    const confirmed = { catalogId: catalog.id, expectedVersion: catalog.version };
    clearEditor();
    const dialog = node("dialog");
    dialog.className = "frdisc-policy-confirm";
    const title = node("h3", "Activate this network catalog?");
    title.id = `${host.id || "network-catalog"}-activation-title`;
    dialog.setAttribute("aria-labelledby", title.id);
    dialog.append(
      title,
      node(
        "p",
        "Locations will add new products from this catalog. Shared product details will be managed here.",
      ),
      node(
        "p",
        "Existing unmatched items stay usable. Location prices, stock and vendor details stay private.",
      ),
    );
    const controls = node("div");
    controls.className = "catalog-actions";
    const cancel = button("Cancel", closeEditor);
    controls.append(
      cancel,
      button("Activate catalog", () => {
        if (
          !current(id, g) ||
          activationScope() !== scope ||
          editor !== dialog ||
          pendingActivations.has(scope) ||
          blocked ||
          !deps.canManage() ||
          blocked ||
          !activationStatus?.ready ||
          activationStatus.catalog?.id !== confirmed.catalogId ||
          activationStatus.catalog.version !== confirmed.expectedVersion
        )
          return;
        const original: PendingActivation = {
          intent: Object.freeze({ ...confirmed, requestId: crypto.randomUUID() }),
          outcome: "unknown",
        };
        pendingActivations.set(scope, original);
        void submitActivation(scope, original);
      }),
    );
    dialog.append(controls);
    editor = dialog;
    host.append(dialog);
    dialog.addEventListener("cancel", (event) => {
      event.preventDefault();
      clearEditor();
    });
    dialog.showModal();
    cancel.focus();
  }
  function renderActivation(): void {
    if (!deps.network || !deps.activationIdentity || !deps.canView()) return;
    const scope = activationScope(),
      original = scope === null ? undefined : pendingActivations.get(scope);
    const panel = node("section");
    panel.className = "catalog-editor";
    panel.setAttribute("aria-label", "Catalog activation");
    const state = activationStatus?.catalog?.state ?? view?.catalog?.state;
    panel.append(
      node(
        "strong",
        original
          ? original.outcome === "not_applied"
            ? "Activation not applied"
            : "Activation unconfirmed"
          : state === "enforced"
            ? "Catalog active"
            : state === "preparation" || activationStatus?.reason === "catalog_unavailable"
              ? "Catalog not activated"
              : "Activation status unavailable",
      ),
    );
    const message =
      activationMessage ||
      (!deps.canManage()
        ? "Catalog management permission is required to activate."
        : !activationStatus
          ? "Activation status unavailable. Reload to check again."
          : activationStatus.reason === "ownership_review_required"
            ? "Catalog ownership needs review."
            : activationStatus.reason === "catalog_unavailable"
              ? "No catalog exists for this network yet, so there is nothing to activate."
              : activationStatus.reason === "deployment_unavailable"
                ? "Activation is waiting for Snip & Sip to finish enabling catalog management. Your products are not the cause."
                : !activationStatus.ready && state !== "enforced"
                  ? "Catalog activation is not available yet."
                  : "");
    if (message) {
      const notice = node("p", message);
      notice.setAttribute("role", "status");
      panel.append(notice);
    }
    const controls = node("div");
    controls.className = "catalog-actions";
    if (deps.canManage() && scope !== null) {
      if (original) {
        if (original.outcome !== "not_applied")
          controls.append(button("Check status", async () => { if (await confirmLeave()) void checkActivation(); }));
        if (original.outcome === "retry_same_request")
          controls.append(
            button("Retry activation", async () => {
              if (original.outcome === "retry_same_request" && await confirmLeave()) void submitActivation(scope, original);
            }),
          );
      } else if (state !== "enforced")
        controls.append(
          button(
            "Activate catalog",
            confirmActivation,
            !activationStatus?.ready ||
              activationStatus.catalog?.id !== view?.catalog?.id ||
              activationStatus.catalog?.version !== view?.catalog?.version,
          ),
        );
    }
    panel.append(controls);
    host.append(panel);
  }
  async function write(path: string, body: unknown, allowed: () => boolean): Promise<unknown> {
    const id = deps.identity(),
      g = generation;
    if (!id || busy || !allowed()) return null;
    busy = true;
    renderBusy();
    try {
      const reply = await deps.request("POST", path, body);
      if (!current(id, g)) return null;
      clearEditor();
      busy = false;
      if (reply.status >= 200 && reply.status < 300) {
        await load(false, "Saved");
        return deps.identity() === id ? reply.body : null;
      }
      blocked = true;
      status =
        reply.status === 409
          ? "Catalog changed. Reload and review before saving."
          : reply.status === 403
            ? "Catalog access changed. Reload to view current access."
            : reply.status === 0 || reply.status >= 500
              ? "Save outcome unconfirmed. Reload before making another choice."
              : errorText(reply.body);
      render();
    } catch {
      if (current(id, g)) {
        busy = false;
        blocked = true;
        clearEditor();
        status = "Save outcome unconfirmed. Reload before making another choice.";
        render();
      }
    }
    return null;
  }
  function renderBusy() {
    host
      .querySelectorAll<HTMLButtonElement | HTMLInputElement | HTMLSelectElement>(
        "button,input,select",
      )
      .forEach((e) => (e.disabled = true));
  }
  function field(form: HTMLFormElement, label: string, name: string, value = "", type = "text") {
    const l = node("label", label),
      input = node("input");
    input.name = name;
    input.type = type;
    input.value = value;
    if (type === "text") input.inputMode = "text";
    input.required = !["brand", "upc"].includes(name);
    l.append(input);
    form.append(l);
    return input;
  }
  function select(
    form: HTMLFormElement,
    label: string,
    name: string,
    options: readonly { id: string; label: string }[],
    value = "",
  ) {
    const l = node("label", label),
      s = node("select");
    s.name = name;
    s.required = true;
    s.append(new Option("Choose", ""));
    for (const o of options) s.append(new Option(o.label, o.id));
    s.value = value;
    l.append(s);
    form.append(l);
    return s;
  }
  async function openEditorForm(title: string) {
    if (!(await confirmLeave())) return undefined;
    clearEditor();
    editor = node("section");
    editor.className = "catalog-editor editor-sheet editor-compact";
    // EDITORUX1: the same header as every franchisor editor, with the same Close.
    const head = node("div");
    head.className = "editor-head";
    const heading = node("h3", title);
    heading.className = "editor-title";
    const close = button("Close", closeEditor);
    close.className = "btn-secondary editor-close";
    head.append(heading, close);
    editor.append(head);
    const f = node("form");
    f.onsubmit = (event) => event.preventDefault();
    f.className = "catalog-fields";
    editor.append(f);
    editorForm = f;
    host.append(editor);
    return f;
  }
  function actions(f: HTMLFormElement, label: string, allowed: () => boolean) {
    const actionsRow = node("div");
    actionsRow.className = "catalog-actions editor-foot";
    const status = node("div");
    status.className = "editor-foot-status";
    const unsaved = createUnsavedIndicator();
    status.append(unsaved.element);
    actionsRow.append(status, button("Cancel", closeEditor));
    if (allowed()) {
      const save = node("button", label);
      save.type = "submit";
      actionsRow.append(save);
    } else
      f.querySelectorAll<HTMLInputElement | HTMLSelectElement>("input,select").forEach(
        (e) => (e.disabled = true),
      );
    f.append(actionsRow);
    editorInitial = formValues(f);
    const paint = () => unsaved.set(editorInitial !== undefined && formValues(f) !== editorInitial);
    f.addEventListener("input", paint);
    f.addEventListener("change", paint);
  }
  /**
   * HQFRAN1 (B3): a network with no kind (or a kind with no category) cannot add a product, so the sheet says what to add first and
   * offers it in place. The new kind or category is made without leaving the sheet's draft: the product editor reopens with
   * everything typed so far and the new choice selected.
   */
  function setupGuide(f: HTMLFormElement): void {
    const box = node("div");
    box.className = "catalog-guide";
    box.dataset["testid"] = "catalog-guide";
    box.setAttribute("role", "status");
    const inline = node("div");
    inline.className = "catalog-guide-inline";
    inline.dataset["testid"] = "catalog-guide-inline";
    inline.hidden = true;
    f.before(box, inline);
    const kindSelect = () => f.elements.namedItem("kindId") as HTMLSelectElement;
    const save = () => f.querySelector<HTMLButtonElement>("button[type=submit]");
    // A change event on any field repaints only when the need changed, so a button the person is pressing is never rebuilt under them.
    let painted = "";
    const paint = () => {
      const kinds = view?.kinds.filter((k) => k.active) ?? [];
      const kindId = kindSelect().value;
      const needsKind = kinds.length === 0;
      const needsCategory =
        !needsKind && kindId !== "" && !(view?.categories ?? []).some((c) => c.active && c.kind_id === kindId);
      const state = needsKind ? "kind" : needsCategory ? `category:${kindId}` : "none";
      if (state === painted) return;
      painted = state;
      box.hidden = !needsKind && !needsCategory;
      box.replaceChildren();
      const submit = save();
      if (submit !== null) submit.disabled = needsKind || needsCategory;
      if (!needsKind && !needsCategory) {
        inline.hidden = true;
        return;
      }
      const text = node(
        "p",
        needsKind
          ? "Add an item type first. An item type says how a product is sold, such as by the pour or by the whole unit."
          : "This item type has no category yet. Add one to save the product.",
      );
      const open = button(needsKind ? "New item type" : "New category", () => openInline(needsKind ? "kind" : "category", kindId));
      open.className = "btn-secondary";
      open.dataset["testid"] = needsKind ? "catalog-guide-new-kind" : "catalog-guide-new-category";
      box.append(text, open);
    };
    const openInline = (what: "kind" | "category", kindId: string) => {
      inline.replaceChildren();
      inline.hidden = false;
      const name = node("input");
      name.type = "text";
      name.inputMode = "text";
      name.setAttribute("aria-label", what === "kind" ? "Item type name" : "Category name");
      name.placeholder = what === "kind" ? "Item type name, for example Whiskey" : "Category name, for example Bourbon";
      const method = node("select");
      method.setAttribute("aria-label", "Selling method");
      for (const [value, label] of [["per_ounce", "By the pour"], ["whole_unit", "Whole unit"], ["both", "Both"]] as const) method.append(new Option(label, value));
      const error = node("p");
      error.className = "field-error";
      error.setAttribute("role", "alert");
      const add = button(what === "kind" ? "Add item type" : "Add category", () => void submitInline());
      add.className = "btn-primary";
      add.dataset["testid"] = "catalog-guide-add";
      const cancel = button("Cancel", () => {
        inline.hidden = true;
      });
      const row = node("div");
      row.className = "catalog-guide-row";
      row.append(name, ...(what === "kind" ? [method] : []), add, cancel);
      inline.append(row, error);
      name.focus();
      const submitInline = async () => {
        const label = name.value.trim();
        const key = keyFromName(label);
        if (key === "") {
          error.textContent = "Enter a name with letters or numbers.";
          name.focus();
          return;
        }
        const id = deps.identity(),
          g = generation;
        if (!id || busy || !manages()) return;
        const draft = readCatalogProductFields(f);
        busy = true;
        add.disabled = true;
        try {
          const reply = await deps.request(
            "POST",
            what === "kind" ? "/kinds" : "/categories",
            what === "kind" ? { label, key, mechanism: method.value } : { label, key, kindId },
          );
          if (!current(id, g)) return;
          busy = false;
          if (reply.status < 200 || reply.status >= 300) {
            add.disabled = false;
            error.textContent = reply.status === 409 ? `${what === "kind" ? "An item type" : "A category"} with that name already exists.` : errorText(reply.body);
            return;
          }
          await load(false, "");
          // A load starts a new generation, so only the signed-in identity is checked from here.
          if (deps.identity() !== id || !view) return;
          const made =
            what === "kind"
              ? view.kinds.find((k) => k.key === key)
              : view.categories.find((c) => c.key === key && c.kind_id === kindId);
          await productEditor(undefined, {
            ...draft,
            ...(what === "kind" ? { kindId: made?.id ?? "", categoryId: "" } : { categoryId: made?.id ?? "" }),
          });
        } catch {
          busy = false;
          add.disabled = false;
          error.textContent = "That could not be saved. Try again.";
        }
      };
    };
    f.addEventListener("change", paint);
    paint();
  }
  async function productEditor(
    product?: CatalogProduct,
    proposal?: CatalogProposal,
    request?: CatalogRequest,
  ) {
    if (!view) return;
    const p = product ? catalogProposal(product) : proposal;
    const memberCatalog = !deps.network && view.ownerKind === "network";
    const requestMode = memberCatalog && !product,
      allowed = memberCatalog && product ? () => false : requestMode ? localWrites : manages;
    const f = await openEditorForm(
      product
        ? allowed()
          ? "Edit product"
          : "View product"
        : requestMode
          ? "Request product"
          : "Add product",
    );
    if (!f) return;
    appendCatalogProductFields(f, view, p, query);
    const requestId = crypto.randomUUID(),
      id = deps.identity(),
      g = generation;
    actions(f, requestMode ? "Send request" : "Save", allowed);
    if (!product && !requestMode && allowed()) setupGuide(f);
    f.onsubmit = (e) => {
      e.preventDefault();
      if (!current(id, g) || !allowed() || !f.reportValidity()) return;
      const base = readCatalogProductFields(f);
      void (async () => {
        const ok = await saveProduct(base, product, requestId, requestMode);
        if (ok && request && deps.identity() === id && manages()) {
          const created = view?.products.find((x) => x.product_id === ok),
            actualRequest = requests.find((r) => r.id === request.id && r.state === "pending");
          if (created && actualRequest) resolveRequest(actualRequest, created);
        }
      })();
    };
  }
  async function saveProduct(
    base: CatalogProposal,
    p: CatalogProduct | undefined,
    requestId: string,
    requestMode: boolean,
  ): Promise<string | null> {
    // Request resolution always remains a deliberate separate operation against its actual current version.
    const result = await write(
      requestMode ? "/requests" : "/products",
      requestMode
        ? { requestId, proposal: base }
        : {
            requestId,
            product: base,
            ...(p ? { id: p.product_id, expectedVersion: p.revision, active: p.active } : {}),
          },
      requestMode ? localWrites : manages,
    );
    return result !== null &&
      typeof result === "object" &&
      "id" in result &&
      typeof result.id === "string"
      ? result.id
      : null;
  }
  async function taxonomy(category: boolean) {
    if (!manages() || !view) return;
    const f = await openEditorForm(category ? "Add category" : "Add item type");
    if (!f) return;
    const labelInput = field(f, "Name", "label");
    const key = field(f, "Key (made from the name)", "key");
    key.pattern = "[a-z][a-z0-9_]*";
    // HQFRAN1 (B3): the key follows the name until someone edits it, so no one has to invent one.
    let keyEdited = false;
    key.addEventListener("input", () => {
      keyEdited = true;
    });
    labelInput.addEventListener("input", () => {
      if (!keyEdited) key.value = keyFromName(labelInput.value);
    });
    if (category)
      select(
        f,
        "Item type",
        "kindId",
        view.kinds.filter((k) => k.active).map((k) => ({ id: k.id, label: k.label })),
      );
    else
      select(f, "Selling method", "mechanism", [
        { id: "per_ounce", label: "By the pour" },
        { id: "whole_unit", label: "Whole unit" },
        { id: "both", label: "Both" },
      ]);
    actions(f, "Save", manages);
    const id = deps.identity(),
      g = generation;
    f.onsubmit = (e) => {
      e.preventDefault();
      if (!current(id, g) || !manages() || !f.reportValidity()) return;
      void write(category ? "/categories" : "/kinds", Object.fromEntries(new FormData(f)), manages);
    };
  }
  async function resolveRequest(r: CatalogRequest, p?: CatalogProduct) {
    if (!manages()) return;
    const f = await openEditorForm(p ? "Resolve request" : "Decline request");
    if (!f) return;
    f.append(node("p", p ? `${p.name} · ${catalogPackage(p)}` : r.proposal.name));
    if (!p) field(f, "Reason", "reason");
    actions(f, p ? "Make available" : "Decline", manages);
    const id = deps.identity(),
      g = generation;
    f.onsubmit = (e) => {
      e.preventDefault();
      if (!current(id, g) || !manages() || !f.reportValidity()) return;
      void write(
        `/requests/${encodeURIComponent(r.id)}/resolve`,
        {
          expectedVersion: r.version,
          productId: p?.product_id ?? null,
          ...(!p ? { reason: String(new FormData(f).get("reason")) } : {}),
        },
        manages,
      );
    };
  }
  async function picker(item?: CatalogLocalItem, request?: CatalogRequest) {
    if (!view) return;
    const f = await openEditorForm(
      item ? "Match existing item" : request ? "Choose existing product" : "Add from catalog",
    );
    if (!f) return;
    if (item)
      f.append(
        node(
          "p",
          `${item.name} · ${item.sizeValue ?? "Size missing"} ${item.sizeUnit ?? ""} · ${item.packageClass ?? "Package missing"}`,
        ),
      );
    const search = field(f, "Search catalog", "search", query);
    search.required = false;
    const list = node("div");
    f.append(list);
    f.append(button("Cancel", closeEditor));
    if (localWrites())
      f.append(
        button(view.ownerKind === "network" ? "Request missing product" : "Add product", () => {
          query = search.value;
          productEditor();
        }),
      );
    const draw = () => {
      list.replaceChildren();
      for (const p of view?.products ?? []) {
        if (
          !p.active ||
          ![p.name, p.brand ?? "", p.upc ?? ""].some((v) =>
            v.toLowerCase().includes(search.value.toLowerCase()),
          )
        )
          continue;
        const row = node("div");
        row.className = "catalog-candidate";
        row.append(
          node("strong", p.name),
          node("span", `${p.brand ?? ""} · ${catalogPackage(p)} · UPC ${p.upc ?? "Not recorded"}`),
        );
        row.append(button("Review", () => reviewCandidate(p, item, request)));
        list.append(row);
      }
    };
    search.oninput = draw;
    draw();
  }
  async function reviewCandidate(p: CatalogProduct, item?: CatalogLocalItem, request?: CatalogRequest) {
    if (!view) return;
    const f = await openEditorForm(
      request ? "Resolve request" : item ? "Match existing item" : "Add to location",
    );
    if (!f) return;
    if (item)
      f.append(
        node(
          "p",
          `${item.name} · ${item.sizeValue ?? "Size missing"} ${item.sizeUnit ?? ""} · ${item.packageClass ?? "Package missing"}`,
        ),
      );
    f.append(
      node(
        "p",
        `${p.name} · ${p.brand ?? ""} · ${catalogPackage(p)} · UPC ${p.upc ?? "Not recorded"}`,
      ),
    );
    f.append(button("Search catalog", () => picker(item, request)));
    if (request) {
      f.append(button("Cancel", closeEditor));
      f.append(button("Make available", () => resolveRequest(request, p), !manages()));
      return;
    }
    const compatible = !item ? p.units_per_package === 1 : catalogCompatible(item, p, view);
    if (!compatible) f.append(node("p", "Product and package need review before confirmation."));
    const kind = view.kinds.find((k) => k.id === p.kind_id);
    const mechanism =
      kind?.mechanism === "both"
        ? select(f, "Local selling method", "localSaleMechanism", [
            { id: "per_ounce", label: "By the pour" },
            { id: "whole_unit", label: "Whole unit" },
          ])
        : undefined;
    actions(f, item ? "Confirm match" : "Add to inventory", localWrites);
    const submit = f.querySelector<HTMLButtonElement>('button[type="submit"]');
    if (submit) submit.disabled = !compatible;
    const id = deps.identity(),
      g = generation,
      requestId = crypto.randomUUID();
    f.onsubmit = (e) => {
      e.preventDefault();
      if (!current(id, g) || !localWrites() || !compatible || !f.reportValidity()) return;
      if (!item) {
        const actualKind = view?.kinds.find((k) => k.id === p.kind_id),
          actualCategory = view?.categories.find((c) => c.id === p.category_id);
        const method = actualKind?.mechanism === "both" ? mechanism?.value : actualKind?.mechanism;
        if (
          !actualKind ||
          !actualCategory ||
          (method !== "per_ounce" && method !== "whole_unit") ||
          !deps.prepareLocalItem
        )
          return;
        clearEditor();
        deps.prepareLocalItem(
          {
            product: p,
            kindKey: actualKind.key,
            categoryKey: actualCategory.key,
            localSaleMechanism: method,
            requestId,
          },
          () => current(id, g) && localWrites(),
          () => {
            if (current(id, g)) void load();
          },
        );
        return;
      }
      void write(
        "/links",
        {
          productId: p.product_id,
          expectedVersion: p.revision,
          requestId,
          ...(item
            ? { localProductId: item.localProductId }
            : mechanism
              ? { localSaleMechanism: mechanism.value }
              : {}),
        },
        localWrites,
      );
    };
  }
  function render() {
    clearEditor();
    host.replaceChildren();
    const heading = node("h2", deps.network ? "Products" : "Product catalog");
    hoverHelp(
      heading,
      "Shared product identity: name, brand, package, item type, category and UPC.",
      "Local vendors, cost, prices, par, stock and active state stay in local Inventory.",
      "Review the exact bottle or package before confirming a catalog link.",
    );
    let content = host;
    if (!deps.network) {
      const summary = node("div");
      summary.className = "catalog-summary";
      const label = node("div");
      label.append(heading);
      const unmatched = items.filter((item) => !item.link).length;
      const count = node(
        "p",
        !view
          ? loadFailed
            ? "Catalog could not be loaded"
            : "Catalog unavailable"
          : !candidatesAvailable
            ? "Inventory matching unavailable"
            : unmatched > 0
              ? `${unmatched} ${unmatched === 1 ? "item needs" : "items need"} matching`
              : items.length
                ? "All inventory items matched"
                : "No inventory items to match",
      );
      count.setAttribute("role", "status");
      label.append(count);
      const toggle = button(catalogExpanded ? "Close catalog" : "Review catalog", async () => {
        if (!(await confirmLeave())) return;
        catalogExpanded = !catalogExpanded;
        render();
        host.querySelector<HTMLButtonElement>(".catalog-summary button")?.focus();
      });
      toggle.setAttribute("aria-expanded", String(catalogExpanded));
      toggle.setAttribute("aria-controls", workspaceId);
      summary.append(label, toggle);
      host.append(summary);
      content = node("section");
      content.id = workspaceId;
      content.className = "catalog-workspace";
      content.setAttribute("aria-label", "Product catalog review");
      content.hidden = !catalogExpanded;
      host.append(content);
      if (!catalogExpanded) return;
    }
    const head = node("div");
    head.className = "catalog-actions";
    if (deps.network) head.append(heading);
    const reload = async () => {
      if (await confirmLeave()) void load(pendingActivation()?.outcome === "not_applied");
    };
    // INVUX1: after a failed read the one control says Retry and sits with the message; otherwise it stays Reload.
    if (!loadFailed)
      head.append(
        button(
          pendingActivation()?.outcome === "not_applied" ? "Reload current catalog" : "Reload",
          reload,
          pendingActivation() !== undefined && pendingActivation()?.outcome !== "not_applied",
        ),
      );
    if (manages()) {
      const addProduct = button("Add product", () => productEditor());
      addProduct.className = "btn-primary";
      head.append(addProduct);
      // INVUX1 (finding 06): kinds and categories are rare setup, one tap behind a named disclosure.
      head.append(
        createSetupDisclosure("catalog-setup", [
          button("Add item type", () => taxonomy(false)),
          button("Add category", () => taxonomy(true)),
        ]),
      );
    }
    if (localWrites()) {
      head.append(button("Add from catalog", () => picker()));
      if (view?.ownerKind === "network")
        head.append(button("Request product", () => productEditor()));
    }
    content.append(head);
    // A refresh that failed leaves no current activation reading; only an unconfirmed activation still needs its panel.
    if (view && (!loadFailed || pendingActivation() !== undefined)) renderActivation();
    if (loadFailed) {
      const failed = node("div");
      failed.className = "catalog-alert catalog-load-failed";
      failed.dataset["testid"] = "catalog-load-failed";
      failed.setAttribute("role", "alert");
      failed.append(node("span", status), button("Retry", reload));
      content.append(failed);
    } else if (status) {
      const msg = node("p", status);
      msg.setAttribute("role", "status");
      content.append(msg);
    }
    if (!view) return;
    const pending = requests.filter((r) => r.state === "pending");
    if (deps.network && pending.length) {
      const alert = node("div");
      alert.className = "catalog-alert";
      alert.append(
        node("span", `${pending.length} product requests need review`),
        button("Review requests", () => inbox.scrollIntoView({ block: "start" })),
      );
      content.append(alert);
    }
    const tools = node("div");
    tools.className = "catalog-actions";
    const search = node("input");
    search.type = "search";
    search.placeholder = "Search name, brand or UPC";
    search.setAttribute("aria-label", "Search catalog");
    search.value = query;
    const kinds = node("select");
    kinds.setAttribute("aria-label", "Item type");
    kinds.append(new Option("All item types", ""));
    for (const k of view.kinds) kinds.append(new Option(k.label, k.id));
    kinds.value = kindFilter;
    tools.append(search, kinds);
    content.append(tools);
    const products = node("div");
    content.append(products);
    const draw = () => {
      query = search.value;
      kindFilter = kinds.value;
      products.replaceChildren();
      for (const p of view?.products ?? []) {
        if (
          (kindFilter && kindFilter !== p.kind_id) ||
          ![p.name, p.brand ?? "", p.upc ?? ""].some((v) =>
            v.toLowerCase().includes(query.toLowerCase()),
          )
        )
          continue;
        const row = node("div");
        row.className = "catalog-product";
        const info = node("div");
        info.append(
          node("strong", p.name),
          node(
            "p",
            `${p.brand ?? ""} · ${catalogPackage(p)} · ${view?.kinds.find((k) => k.id === p.kind_id)?.label ?? ""} · ${view?.categories.find((c) => c.id === p.category_id)?.label ?? ""}`,
          ),
          node("small", `UPC ${p.upc ?? "Not recorded"}${p.active ? "" : " · Inactive"}`),
        );
        row.append(
          info,
          button(manages() ? "Edit" : "View", () => productEditor(p)),
        );
        if (manages() && p.active)
          row.append(
            button("Deactivate", async () => {
              if (await confirmLeave() && window.confirm(`Deactivate ${p.name}?`))
                void write(
                  "/products",
                  {
                    id: p.product_id,
                    expectedVersion: p.revision,
                    requestId: crypto.randomUUID(),
                    active: false,
                    product: catalogProposal(p),
                  },
                  manages,
                );
            }),
          );
        products.append(row);
      }
      if (!products.childElementCount) {
        // INVUX1 (finding 01): an empty catalog and a filter that matches nothing are different states.
        const empty = node("div");
        empty.className = "catalog-empty";
        empty.setAttribute("role", "status");
        if (view?.products.length) {
          const kindLabel = view.kinds.find((k) => k.id === kindFilter)?.label;
          const what = [query.trim() ? `"${query.trim()}"` : "", kindLabel ?? ""].filter(Boolean);
          empty.dataset["testid"] = "catalog-filtered-empty";
          empty.append(
            node("strong", "No matching products"),
            node("p", `Nothing in the catalog matches ${what.join(" in ")}.`),
            button("Clear filters", () => {
              search.value = "";
              kinds.value = "";
              draw();
            }),
          );
        } else {
          empty.dataset["testid"] = "catalog-empty";
          empty.append(node("strong", "No products yet"));
          if (manages()) {
            const first = button("Add first product", () => productEditor());
            // One primary action per page: the toolbar's Add product keeps the accent, this is its quiet twin.
            first.dataset["testid"] = "catalog-add-first-product";
            empty.append(node("p", "Add the first product to start the catalog."), first);
          }
        }
        products.append(empty);
      }
    };
    search.oninput = draw;
    kinds.onchange = draw;
    draw();
    if (!deps.network) {
      content.append(node("h3", "Existing inventory items"));
      // UIFIX5: the match state is said once, in a banner, not on every row.
      const unmatched = items.filter((item) => !item.link).length;
      if (!candidatesAvailable)
        content.append(
          node("p", candidatesMessage || "Inventory matching unavailable. Reload to try again."),
        );
      if (candidatesAvailable && unmatched > 0) {
        const banner = node("div");
        banner.className = "catalog-alert";
        banner.setAttribute("role", "status");
        banner.append(
          node(
            "span",
            `${unmatched} ${unmatched === 1 ? "item is" : "items are"} not matched to the catalog yet.`,
          ),
        );
        content.append(banner);
      }
      for (const item of items) {
        const row = node("div");
        row.className = "catalog-product";
        row.append(
          node(
            "span",
            `${item.name}${item.link ? " · Confirmed link" : ""}${item.active ? "" : " · Inactive locally"}`,
          ),
          button(
            item.link ? "Review match" : "Match to catalog",
            () => picker(item),
            !localWrites(),
          ),
        );
        content.append(row);
      }
    }
    const inbox = node("section");
    inbox.append(node("h3", "Product requests"));
    for (const r of requests) {
      const row = node("div");
      row.className = "catalog-product";
      row.append(
        node(
          "span",
          `${r.proposal.name} · ${r.locationName} · ${r.state}${r.reason ? ` · ${r.reason}` : ""}`,
        ),
      );
      if (manages() && r.state === "pending")
        row.append(
          button("Add to catalog", () => productEditor(undefined, r.proposal, r)),
          button("Use existing product", () => picker(undefined, r)),
          button("Decline", () => resolveRequest(r)),
        );
      if (localWrites() && r.state === "declined")
        row.append(button("Revise request", () => productEditor(undefined, r.proposal)));
      inbox.append(row);
    }
    if (!requests.length)
      inbox.append(
        node("p", requestsAvailable ? "No product requests" : "Product requests unavailable"),
      );
    if (!deps.network || deps.canManage()) content.append(inbox);
  }
  return {
    load,
    confirmLeave,
    reset() {
      generation++;
      activationSequence++;
      activationStatus = undefined;
      activationMessage = "";
      view = undefined;
      requests = [];
      requestsAvailable = false;
      candidatesAvailable = false;
      candidatesMessage = "";
      catalogExpanded = false;
      catalogIdentity = null;
      items = [];
      blocked = false;
      busy = false;
      status = "";
      query = "";
      kindFilter = "";
      clearEditor();
      host.replaceChildren();
    },
  };
}
